import React, {
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';
import {
  NavbarContainer,
  ToggleSidebarButton,
  RightSection,
  Dropdown,
  DropdownItem,
  NavMenu,
  NavMenuCard,
  NavMenuHeader,
  MenuButton,
} from './styles';

import { MdMenu, MdClose } from 'react-icons/md';

import useAuth from '~/hooks/useAuth';
import {
  FaBuilding,
  FaRegCircleUser,
  FaUserPen,
  FaGem,
  FaRightFromBracket,
  FaStore,
  FaRegBell,
  FaRegRectangleXmark,
  FaRectangleXmark,
  FaBell,
  FaCircleUser,
} from 'react-icons/fa6';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { FilterContext } from '~/context/filter';
import { InformacaoTelasAcessadasContext } from '~/context/InformacaoTelasAcessadas';
import { NavigationContext } from '~/context/navigation';
import api from '~/services/api';
import { DropdownUserProps, Loja } from './types';
import { toast } from 'react-toastify';
import { Context } from '~/context/auth';
import { LojaContext } from '~/context/loja';
import { useLojasUsuario } from './hooks/LojaPorUsuario';
import Spinner from '../Spinner';
import { useNotificacoes } from './hooks/Notificacoes';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useLayout } from '../Layout/LayoutContext';
import { useHistory } from 'react-router-dom';
import { StorageService } from '~/utils/classes/StorageService';

interface NavbarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
}

const MySwal = withReactContent(Swal);

const Navbar: React.FC<NavbarProps> = ({ collapsed, setCollapsed }) => {
  moment.locale('pt-br');
  const history = useHistory();
  const { telas } = useLayout();
  const user = useAuth();
  const store = new StorageService();

  const [activeMenu, setActiveMenu] = useState<
    'usuario' | 'notificacoes' | 'lojas' | null
  >(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const { screens, removeAllScreens } = useContext(NavigationContext);
  const { RemoveAllFilterPages } = useContext(FilterContext);
  const { removeAllScreenInfo } = useContext(InformacaoTelasAcessadasContext);

  const { handleLogout, handleChangeUserData, handleNotificacao } =
    useContext(Context);

  const { codLoja, changeLoja } = useContext(LojaContext);

  const confirmaFechamento = useCallback(async () => {
    const confirm = await MySwal.fire({
      title: ``,
      text: 'Deseja fechar todas as telas?',
      showCancelButton: true,
      confirmButtonColor: '#0065FF',
      cancelButtonColor: '#DE350B',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    }).then(async (result: any) => {
      return result.isConfirmed;
    });
    return confirm;
  }, []);

  const handleCloseScreens = async () => {
    const confirmSearchWithoutFilter = await confirmaFechamento();

    if (confirmSearchWithoutFilter) {
      if (screens.length > 1) {
        RemoveAllFilterPages();
        removeAllScreenInfo();
        removeAllScreens();
      }
    }
  };

  /**
   * Define loja padrão
   */
  const fetchLojaData = async (codloja: number) => {
    const [lojaRes, libRes] = await Promise.all([
      api.get(`/lojas/${codloja}`),
      api.get(`/usuario/buscar-liberador-financeiro/${codloja}`),
    ]);

    const { success, message, data } = lojaRes.data;
    if (!success) throw new Error(message);

    const regime = data.tipo_regime;
    const flgLibFinanceiro = libRes.data?.data?.flg_lib_financeiro ?? false;

    return { regime, flgLibFinanceiro };
  };

  const handleLojaStorage = useCallback(
    async (codloja: number) => {
      try {
        const { regime, flgLibFinanceiro } = await fetchLojaData(codloja);
        handleChangeUserData({
          ...user.user,
          loja: codloja,
          tipo_regime: regime,
          flg_lib_financeiro: flgLibFinanceiro,
        });
      } catch (e: any) {
        toast.error(e.message);
      }
    },
    [handleChangeUserData, user.user],
  );

  const updateLojaUsuario = async (userId: number, codloja: number) => {
    const res = await api.put(`/usuarios/loja/${userId}`, {
      cod_loja: codloja,
    });
    if (!res.data?.success)
      throw new Error(res.data?.message || 'Erro ao atualizar loja');
    return res.data.message;
  };

  const handleSetLoja = useCallback(
    async (codloja: number) => {
      try {
        if (!user.user) return;
        const message = await updateLojaUsuario(user?.user?.id, codloja);
        await handleLojaStorage(codloja);
        changeLoja(codloja);
        toast.success(message);
      } catch (e: any) {
        toast.error(e.message);
      }
    },
    [user.user, handleLojaStorage, changeLoja],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Busca Lojas disponíveis
   */
  const [selectLojas, setSelectLojas] = useState<Loja[]>([]);

  const {
    data: lojasData,
    isLoading: isFetchingLojas,
    // error,
  } = useLojasUsuario(!!user.user?.id);

  useEffect(() => {
    if (lojasData) {
      const options = lojasData.map((lojas: Loja) => ({
        des_loja: `${lojas.cod_loja.toString().padStart(2, '0')} - ${
          lojas.des_loja
        } (${lojas.des_cidade})`,
        cod_loja: lojas.cod_loja,
      }));
      setSelectLojas(options);
    }
  }, [lojasData]);

  /**
   * Busca Notificações
   */
  const [notificacoes, setNotificacoes] = useState<any>([]);

  const handleOpenNotificacao = (cod_notificacao: number) => {
    const telaNotificacao = telas
      .flatMap((modulo) => modulo.telas)
      .find((tela) => tela.cod_tela === 205);
    if (!telaNotificacao) return;

    const isTelaAberta = screens.some(
      (s) => s.cod_tela === telaNotificacao.cod_tela,
    );

    if (screens.length >= 6 && isTelaAberta) {
      setActiveMenu(null);
      return;
    }

    history.push('/central-notificacao');
    setActiveMenu(null);
    handleNotificacao(cod_notificacao);
  };

  const {
    data: notificacoesData,
    isLoading: isFetchingNotificacoes,
    refetch: refetchNotificacoes,
  } = useNotificacoes(true);

  useEffect(() => {
    if (notificacoesData) {
      setNotificacoes(notificacoesData);
    }
  }, [notificacoesData]);

  /**
   * Navegação Menu Usuário
   */
  const dropdownUserArray: DropdownUserProps[] = [
    { label: 'Minha Empresa', to: '/loja', icon: FaBuilding },
    { label: 'Edição da Conta', to: '/edicao-conta', icon: FaUserPen },
    { label: 'Meu Plano', to: '/meu-plano', icon: FaGem },
  ];

  const isUserRestricted = user?.user?.cod_controle === 2;

  const handleDropdownClick = (to: string) => {
    if (isUserRestricted && to === '/edicao-conta') {
      toast.warning(
        'Edição de conta não permitida, fale com o administrador do sistema',
      );
      return;
    }
    history.push(to);
    // addScreenInfo
    setActiveMenu(null);
  };

  // useEffect(() => {
  //   if (openMenu) {
  //     document.body.style.overflow = 'hidden';
  //   } else {
  //     document.body.style.overflow = '';
  //   }

  //   return () => {
  //     document.body.style.overflow = '';
  //   };
  // }, [openMenu]);

  return (
    <NavbarContainer collapsed={collapsed}>
      <ToggleSidebarButton
        onClick={() => {
          setCollapsed(!collapsed);
          store.save('session', 'collapsed', !collapsed);
        }}
      >
        {collapsed ? <MdMenu size={24} /> : <MdClose size={24} />}
      </ToggleSidebarButton>

      <RightSection ref={menuRef}>
        {/* Fechar todas as telas */}
        <MenuButton>
          <NavMenu
            onClick={() => handleCloseScreens()}
            title="Fechar todas as telas"
          >
            <FaRectangleXmark color="#fff" size={20} />
          </NavMenu>
        </MenuButton>

        {/* Menu de Lojas */}
        <MenuButton>
          <NavMenu
            onClick={() =>
              setActiveMenu(activeMenu === 'lojas' ? null : 'lojas')
            }
            title="Lojas"
          >
            <FaStore color="#fff" size={20} />
          </NavMenu>
          {activeMenu === 'lojas' && (
            <NavMenuCard>
              <NavMenuHeader>Selecione sua loja padrão</NavMenuHeader>
              {isFetchingLojas && (
                <NavMenuHeader>
                  <Spinner size={20} />
                </NavMenuHeader>
              )}
              {!isFetchingLojas &&
                selectLojas.map((loja) => (
                  <DropdownItem
                    key={loja.cod_loja}
                    onClick={() => handleSetLoja(loja.cod_loja)}
                    selected={codLoja === loja.cod_loja}
                  >
                    {loja.des_loja}
                  </DropdownItem>
                ))}
            </NavMenuCard>
          )}
        </MenuButton>

        {/* Menu de Notificações */}
        <MenuButton>
          <NavMenu
            onClick={() => {
              refetchNotificacoes();
              setActiveMenu(
                activeMenu === 'notificacoes' ? null : 'notificacoes',
              );
            }}
            title="Notificações"
          >
            <FaBell color="#fff" size={20} />
          </NavMenu>
          {activeMenu === 'notificacoes' && (
            <NavMenuCard>
              {!isFetchingNotificacoes && notificacoes.length <= 0 && (
                <NavMenuHeader>Nenhuma nova notificação</NavMenuHeader>
              )}
              {!isFetchingNotificacoes &&
                notificacoes.map((notificacao: any) => {
                  const label = `#${notificacao.cod_notificacao} - ${
                    notificacao.des_nome
                  } -
                                                  ${moment(
                                                    notificacao.dta_cadastro_view,
                                                    'DD/MM/YYYY HH:mm:ss',
                                                  ).fromNow()}`;
                  return (
                    <DropdownItem
                      key={notificacao.cod_notificacao}
                      onClick={() => {
                        handleOpenNotificacao(notificacao.cod_notificacao);
                      }}
                    >
                      {label}
                    </DropdownItem>
                  );
                })}
            </NavMenuCard>
          )}
        </MenuButton>

        {/* Menu de Usuário */}
        <MenuButton>
          <NavMenu
            onClick={() =>
              setActiveMenu(activeMenu === 'usuario' ? null : 'usuario')
            }
          >
            <FaCircleUser color="#fff" size={20} />
            {user?.user?.usuario}
          </NavMenu>

          {activeMenu === 'usuario' && (
            <Dropdown>
              {dropdownUserArray.map(({ label, to, icon: Icon }) => (
                <DropdownItem key={to} onClick={() => handleDropdownClick(to)}>
                  <Icon />
                  {label}
                </DropdownItem>
              ))}
              <DropdownItem onClick={() => handleLogout(screens)}>
                <FaRightFromBracket />
                Sair
              </DropdownItem>
            </Dropdown>
          )}
        </MenuButton>
      </RightSection>
    </NavbarContainer>
  );
};

export default Navbar;
